import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useError } from "../../contexts/ErrorContext";
import useApi from "../../hooks/useGaneshApi";

import { Box, Breadcrumbs, Typography } from "@mui/material";

import { OmniSummary_T } from "../../types/Brand";

import { formatDateHumanReadable } from "utils/formatting";
import OmniCountriesSummaryTable from "../../components/Brand/Reports/OmniCountriesSummaryTable";

const DataOpsOmniDetailPage: React.FC = () => {
  const { showError } = useError();
  const { apiRequest } = useApi();

  const { omni_key } = useParams<{ omni_key: string }>();
  document.title = `Store Locator: ${omni_key}`;

  const [omniMetadata, setOmniMetadata] = useState<OmniSummary_T>({});
  const [metadataLoading, setMetadataLoading] = useState<boolean>(true);

  useEffect(() => {
    setMetadataLoading(true);

    if (omni_key) {
      const fetchOmniMetadata = async () => {
        try {
          const response = await apiRequest<OmniSummary_T>(
            `/brand_reports/omni_metadata/${omni_key}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setOmniMetadata(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(`Failed to fetch omni metadata for ${omni_key}.`);
        } finally {
          setMetadataLoading(false);
        }
      };

      fetchOmniMetadata();
    } else {
      console.warn("No omni_key provided");
      setMetadataLoading(false);
    }
  }, [omni_key]);

  return (
    <>
      {metadataLoading ? (
        <div>Loading...</div>
      ) : (
        <Box>
          <h1>Store Locator: {omni_key}</h1>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <RouterLink
              to="/data-ops/omnis/summary"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Store Locators
            </RouterLink>
            <Typography color="textPrimary">{omni_key}</Typography>
          </Breadcrumbs>
          <div>
            <div className="txt-pad-b-5">
              <strong>Omni Key:</strong> {omniMetadata.omni_key}
            </div>
            <div className="txt-pad-b-5">
              <strong>External ID:</strong> {omniMetadata.omni_external_id}
            </div>
            <div className="txt-pad-b-5">
              <strong>Brand ID:</strong> {omniMetadata.brand_id}
            </div>
            <div className="txt-pad-b-5">
              <strong>Brand Name:</strong> {omniMetadata.brand_name}
            </div>
            <div className="txt-pad-b-5">
              <strong>Data Source ID:</strong> {omniMetadata.data_source_id}
            </div>
            <div className="txt-pad-b-5">
              <strong>Chain ID:</strong> {omniMetadata.chain_id}
            </div>
            <div className="txt-pad-b-5">
              <strong>Data Source URL:</strong>{" "}
              <a href={omniMetadata.data_source_browseable_url}>
                {omniMetadata.data_source_browseable_url}
              </a>
            </div>
            <div className="txt-pad-b-5">
              <strong>Data Source Created:</strong>{" "}
              {omniMetadata.data_source_created_at
                ? formatDateHumanReadable(
                    new Date(omniMetadata.data_source_created_at)
                  )
                : "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Frozen At:</strong>{" "}
              {omniMetadata.frozen_at
                ? formatDateHumanReadable(new Date(omniMetadata.frozen_at))
                : "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Data Last Updated:</strong>{" "}
              {omniMetadata.query_last_executed
                ? formatDateHumanReadable(
                    new Date(omniMetadata.query_last_executed)
                  )
                : "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Top Listed Classification:</strong>{" "}
              {omniMetadata.top_listed_classification || "N/A"}
            </div>
          </div>
          <OmniCountriesSummaryTable omni_key={omni_key} />
        </Box>
      )}
    </>
  );
};

export default DataOpsOmniDetailPage;
